import { Classes } from "@blueprintjs/core";
import uniqBy from "lodash/uniqBy";

import { allowBuyCredits } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { OverlapMeetingBanner } from "@/components/pieces/coaching/meetings/OverlapMeetingBanner";
import { UpcomingMeetings } from "@/components/pieces/coaching/meetings/UpcomingMeetings";
import { OngoingReviews } from "@/components/pieces/coaching/reviews/OngoingReviews";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { useCoachingDashboardQuery } from "@/graphql";
import { Assignment } from "@/types/coaches";
import { CoachingUser } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";
import { parseDecimal } from "@/utils/string";

import { Balances } from "./Balances";
import { BuyCreditsBanner } from "./BuyCreditsBanner";
import { Coaches } from "./Coaches";
import { CoachOnboarding } from "./CoachOnboarding";
import { MiniProfile } from "./MiniProfile";
import { UnfulfilledOrders } from "./UnfulfilledOrders";

const STATES = {
  CREDITS: { NO_CREDITS: "NO_CREDITS", HAS_CREDITS: "HAS_CREDITS" },
  MEETINGS: { NO_MEETINGS: "NO_MEETINGS", HAS_MEETINGS: "HAS_MEETINGS" },
  REVIEWS: { NO_REVIEWS: "NO_REVIEWS", HAS_REVIEWS: "HAS_REVIEWS" },
  USER_TYPE: { CLIENT: "CLIENT", COACH: "COACH" },
};

interface DashboardState {
  CREDITS: string;
  MEETINGS: string;
  REVIEWS: string;
  USER_TYPE: string;
  hasUnfulfilledOrders: boolean;
}

const DASHBOARD_COMPONENTS = [
  {
    name: "unfulfilled_orders",
    visibleIf: (state: DashboardState) => state.hasUnfulfilledOrders,
  },
  {
    name: "overlap_meeting_banner",
    visibleIf: (state: DashboardState) =>
      state.MEETINGS === STATES.MEETINGS.HAS_MEETINGS,
  },
  {
    name: "coach_onboarding",
    visibleIf: (state: DashboardState) =>
      state.USER_TYPE === STATES.USER_TYPE.COACH,
  },
  {
    name: "profile",
    visibleIf: (state: DashboardState) =>
      (state.USER_TYPE === STATES.USER_TYPE.CLIENT &&
        state.REVIEWS === STATES.REVIEWS.HAS_REVIEWS &&
        state.MEETINGS === STATES.MEETINGS.HAS_MEETINGS) ||
      state.USER_TYPE === STATES.USER_TYPE.COACH,
  },
  {
    name: "coaches",
    visibleIf: () => true,
  },
  {
    name: "balances",
    visibleIf: () => true,
  },
  {
    name: "meetings",
    visibleIf: () => true,
  },
  {
    name: "reviews",
    visibleIf: () => true,
  },
  {
    name: "buy_credits",
    visibleIf: (state: DashboardState) =>
      state.USER_TYPE === STATES.USER_TYPE.CLIENT,
  },
];

function getDashboardPieces(user: CoachingUser) {
  const numAvailableCredits = parseDecimal(user.availableCredits);

  // Determine state based on user data
  const state: DashboardState = {
    CREDITS:
      numAvailableCredits > 0
        ? STATES.CREDITS.HAS_CREDITS
        : STATES.CREDITS.NO_CREDITS,
    MEETINGS:
      user.upcomingMeetings?.length || user.bookingRequestMeetings?.length
        ? STATES.MEETINGS.HAS_MEETINGS
        : STATES.MEETINGS.NO_MEETINGS,
    REVIEWS: user.ongoingReviews?.length
      ? STATES.REVIEWS.HAS_REVIEWS
      : STATES.REVIEWS.NO_REVIEWS,
    USER_TYPE: user.isAgent ? STATES.USER_TYPE.COACH : STATES.USER_TYPE.CLIENT,
    hasUnfulfilledOrders: !!user.unfulfilledOrders?.length,
  };

  // Return filtered component names
  return DASHBOARD_COMPONENTS.filter((component) =>
    component.visibleIf(state),
  ).map((component) => component.name);
}

export function assignmentsUniqByCoach(
  assignments: Assignment[],
  workspaceId?: string,
): Assignment[] {
  const assignmentInWorkspace = assignments.filter((a) =>
    workspaceId
      ? a.workspace && a.workspace.id === workspaceId
      : a.workspace === null,
  );

  const coachIds = assignmentInWorkspace.map(
    (assignment) => assignment.coach?.id,
  );

  const assignmentNotInWorkspace = uniqBy(
    assignments.filter(
      (assignment) => !coachIds.includes(assignment.coach?.id),
    ),
    (assignment) => assignment.coach?.id,
  );

  return assignmentInWorkspace.concat(assignmentNotInWorkspace);
}

interface CoachingDashboardProps {
  viewAsCoach: boolean;
  customerId?: string;
  currentWorkspaceId?: string;
}

export function CoachingDashboard({
  viewAsCoach,
  customerId,
  currentWorkspaceId,
}: CoachingDashboardProps) {
  const { currentWorkspace } = useAuth();

  const { data, loading } = useCoachingDashboardQuery({
    variables: {
      customerId,
      workspaceId: currentWorkspaceId,
    },
    fetchPolicy: "network-only",
  });

  if (!data || loading) {
    return <Box mt={4} height={300} className={Classes.SKELETON} />;
  }

  const user: CoachingUser | null | undefined = customerId
    ? data.customer
    : data.currentUser;

  if (!user) return <PermissionErrorPage />;

  const sessionsLink = customerId
    ? `/client/${customerId}/sessions`
    : undefined;

  const reviewsLink = customerId ? `/client/${customerId}/reviews` : undefined;

  const assignments = data
    ? assignmentsUniqByCoach(
        ensureNonNullArray(data.assignments),
        currentWorkspaceId,
      )
    : [];

  const upcomingMeetings = ensureNonNullArray(user?.upcomingMeetings);
  const showCoachOnboarding =
    user.agentProfile && !user.agentProfile.paymentOnboardingReady;

  const components: Record<string, React.ReactNode> = {
    unfulfilled_orders: <UnfulfilledOrders user={user} />,
    overlap_meeting_banner: (
      <OverlapMeetingBanner meetings={upcomingMeetings} user={user} />
    ),
    coaches: (
      <Coaches
        user={user}
        assignments={assignments}
        viewAsCoach={viewAsCoach}
        workspaceId={currentWorkspace.id}
      />
    ),
    balances: <Balances user={user} viewAsCoach={viewAsCoach} />,
    meetings: (
      <UpcomingMeetings
        user={user}
        customerId={customerId}
        sessionsLink={sessionsLink}
      />
    ),
    reviews: (
      <OngoingReviews
        user={user}
        customerId={customerId}
        reviewsLink={reviewsLink}
      />
    ),
    profile: <MiniProfile user={user} />,
    // Just show BuyCreditsBanner on personal mode
    buy_credits:
      allowBuyCredits() && currentWorkspace.isPersonal && !user.isAgent ? (
        <>
          <BuyCreditsBanner user={user} />
        </>
      ) : null,
    coach_onboarding: showCoachOnboarding && <CoachOnboarding user={user} />,
  };

  const dashboardPieces = getDashboardPieces(user);

  return (
    <FadeInBox>
      {dashboardPieces.map((piece, index) =>
        components[piece] ? (
          <Box key={index} mt={index > 0 ? 3 : 0}>
            {components[piece]}
          </Box>
        ) : null,
      )}
    </FadeInBox>
  );
}
